import { supabase } from '../lib/supabase'
import {
  Module,
  ModuleWithPermissions,
  UserGroup,
  CreateModuleData,
  UpdateModuleData,
  CreateUserGroupData,
  UpdateUserGroupData,
  ApiResponse,
} from '../types'

// =====================================================
// SERVIÇOS DE MÓDULOS
// =====================================================

export const modulesService = {
  // Obter todos os módulos
  async getAll(): Promise<ApiResponse<Module[]>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .order('order_index', { ascending: true })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulos do usuário atual
  async getUserModules(): Promise<ApiResponse<ModuleWithPermissions[]>> {
    try {
      const { data, error } = await supabase
        .rpc('get_user_modules')

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulos ativos com permissões para um grupo específico
  async getActiveWithPermissionsByGroup(userGroupId: string): Promise<ApiResponse<ModuleWithPermissions[]>> {
    try {
      // Buscar módulos ativos ordenados por order_index
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true })

      if (modulesError) throw modulesError

      // Buscar permissões do grupo
      const { data: permsData, error: permsError } = await supabase
        .from('permissions')
        .select('module_id, can_show, can_insert, can_update, can_delete')
        .eq('user_group_id', userGroupId)

      if (permsError) throw permsError

      const permsByModuleId = new Map<string, { can_show: boolean; can_insert: boolean; can_update: boolean; can_delete: boolean }>(
        (permsData || []).map((p: any) => [
          p.module_id,
          {
            can_show: !!p.can_show,
            can_insert: !!p.can_insert,
            can_update: !!p.can_update,
            can_delete: !!p.can_delete
          }
        ])
      )

      const result: ModuleWithPermissions[] = (modulesData || []).map((m: any) => {
        const p = permsByModuleId.get(m.id)
        return {
          ...m,
          can_show: p ? p.can_show : false,
          can_insert: p ? p.can_insert : false,
          can_update: p ? p.can_update : false,
          can_delete: p ? p.can_delete : false
        } as ModuleWithPermissions
      })

      return { data: result, success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter módulo por ID
  async getById(id: string): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Obter módulo por nome
  async getByName(name: string): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('name', name)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Obter módulo por rota
  async getByRoute(route: string): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .eq('route', route)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Criar módulo
  async create(moduleData: CreateModuleData): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .insert(moduleData)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Atualizar módulo
  async update(id: string, moduleData: UpdateModuleData): Promise<ApiResponse<Module>> {
    try {
      const { data, error } = await supabase
        .from('modules')
        .update(moduleData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as Module, error: (error as Error).message, success: false }
    }
  },

  // Deletar módulo
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('modules')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  }
}

// =====================================================
// SERVIÇOS DE GRUPOS DE USUÁRIOS
// =====================================================

export const userGroupsService = {
  // Obter todos os grupos
  async getAll(): Promise<ApiResponse<UserGroup[]>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .order('display_name', { ascending: true })

      if (error) throw error

      return { data: data || [], success: true }
    } catch (error) {
      return { data: [], error: (error as Error).message, success: false }
    }
  },

  // Obter grupo por ID
  async getById(id: string): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Criar grupo
  async create(groupData: CreateUserGroupData): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .insert(groupData)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Atualizar grupo
  async update(id: string, groupData: UpdateUserGroupData): Promise<ApiResponse<UserGroup>> {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .update(groupData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      return { data, success: true }
    } catch (error) {
      return { data: {} as UserGroup, error: (error as Error).message, success: false }
    }
  },

  // Deletar grupo
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('user_groups')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { data: undefined, success: true }
    } catch (error) {
      return { data: undefined, error: (error as Error).message, success: false }
    }
  }
}
